/* ColetaPublica — Painel administrativo da Coleta Pública Semanal.
 * Atualiza a agregação pública e exibe o tipo de fonte dos dados.
 *
 * Modos suportados:
 *   - internal_aggregation  (default no MVP)
 *   - mock_demo
 *   - real_collection       (Crossref/OpenAlex/Semantic Scholar)
 */
import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import {
  CalendarClock, Play, RefreshCcw, AlertTriangle, CheckCircle2, ExternalLink,
  Globe2, Database, Sparkles, Shield, Clock, History,
} from "lucide-react";
import DataSourceBadge from "@/components/DataSourceBadge";
import ModuleHelp from "@/components/help/ModuleHelp";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");
const fmtDateShort = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const fmtNum = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);

const MODE_ICON = {
  real_collection: Globe2,
  internal_aggregation: Database,
  mock_demo: Sparkles,
};
const MODE_LABEL = {
  real_collection: "Coleta real em fontes abertas",
  internal_aggregation: "Agregação interna (base monitorada)",
  mock_demo: "Demonstração (dados simulados)",
};
const MODE_BUTTON_LABEL = {
  real_collection: "Executar coleta real agora",
  internal_aggregation: "Atualizar agregação pública agora",
  mock_demo: "Gerar dados de demonstração",
};

export default function ColetaPublica() {
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [overview, setOverview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [modes, setModes] = useState({ default: "internal_aggregation", modes: [] });
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [selectedMode, setSelectedMode] = useState("");

  const load = async () => {
    setErr("");
    try {
      const [{ data: r }, { data: o }, { data: m }, { data: md }, { data: sc }] = await Promise.all([
        api.get("/public-admin/runs"),
        api.get("/public/overview"),
        api.get("/public/metadata"),
        api.get("/public-admin/modes"),
        api.get("/public-admin/scheduler-status"),
      ]);
      setRuns(r || []);
      setOverview(o);
      setMeta(m);
      setModes(md || { default: "internal_aggregation", modes: [] });
      setSchedulerStatus(sc);
      if (!selectedMode) setSelectedMode(md?.default || "internal_aggregation");
    } catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const trigger = async () => {
    setRunning(true); setErr(""); setInfo("");
    try {
      const url = selectedMode ? `/public-admin/run?mode=${selectedMode}` : "/public-admin/run";
      const { data } = await api.post(url);
      if (data.ok) {
        setInfo(
          `${data.data_source_label || "Agregação"} concluída em ${data.duration_seconds}s — ` +
          `${data.total_countries} países, ${data.total_documents} documentos publicados no mapa.`,
        );
        await load();
      } else {
        setErr(data.error || "Falha na atualização da agregação pública");
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setRunning(false); }
  };

  const last = runs[0];
  const isReal = selectedMode === "real_collection";

  return (
    <div className="space-y-5" data-testid="coleta-publica-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mil text-[var(--gold)] flex items-center gap-1.5">
            <Globe2 size={11} /> Coleta Pública · Observatório
          </div>
          <h1 className="font-display font-bold text-3xl mt-1 flex items-center gap-3">Coleta Pública Semanal <ModuleHelp module="coletaPublica" /></h1>
          <p className="text-xs text-[var(--text-muted)] mt-1 max-w-3xl">
            Atualiza os dados públicos do Observatório. Disponíveis na{" "}
            <a href="/" target="_blank" rel="noreferrer"
               className="text-[var(--gold)] hover:underline inline-flex items-center gap-1">
              área pública <ExternalLink size={10} />
            </a>.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={load} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="btn-refresh">
            <RefreshCcw size={12} /> Atualizar
          </button>
          <button
            onClick={trigger}
            disabled={running}
            className={`text-xs flex items-center gap-1.5 ${isReal ? "btn-success" : "btn-gold"}`}
            data-testid="btn-run-now"
            title={isReal ? "Buscará nas APIs externas (Crossref/OpenAlex/Semantic Scholar)" : ""}
          >
            <Play size={12} /> {running ? "Executando…" : (MODE_BUTTON_LABEL[selectedMode] || "Atualizar agregação pública agora")}
          </button>
        </div>
      </div>

      {/* Aviso de privacidade */}
      <div className="card-mil p-3 border-l-4 border-[var(--gold)] flex items-start gap-2.5 text-xs" data-testid="privacy-notice">
        <Shield size={14} className="text-[var(--gold)] mt-0.5 shrink-0" />
        <div>
          <div className="font-display font-semibold text-[var(--text)]">Proteção de dados internos</div>
          <div className="text-[var(--text-muted)] mt-0.5 leading-relaxed">
            Os dados públicos exibem apenas informações agregadas — totais por país, área temática e período.
            Documentos, autores, usuários e registros internos <strong>não são expostos</strong> na área pública.
          </div>
        </div>
      </div>

      {/* Painel de Modo de Fonte dos Dados */}
      <div className="card-mil p-4" data-testid="data-source-mode-panel">
        <div className="label-mil text-[10px] mb-2">Modo de fonte dos dados públicos</div>
        <div className="flex items-start gap-3 mb-3 flex-wrap">
          <DataSourceBadge
            mode={meta?.data_source_mode}
            shortLabel={meta?.data_source_short_label}
            note={meta?.methodology_note}
            variant="block"
            className="flex-1 min-w-[260px]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
            Próxima execução em:
          </span>
          {(modes.modes || []).map((m) => {
            const Ico = MODE_ICON[m.code] || Database;
            const isActive = selectedMode === m.code;
            return (
              <button
                key={m.code}
                onClick={() => setSelectedMode(m.code)}
                className={`text-[11px] px-2.5 py-1.5 rounded border flex items-center gap-1.5 transition ${
                  isActive
                    ? "bg-[var(--gold)]/15 border-[var(--gold)] text-[var(--text)] font-semibold"
                    : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--gold)]"
                }`}
                title={m.note}
                data-testid={`mode-pick-${m.code}`}
              >
                <Ico size={11} /> {MODE_LABEL[m.code] || m.label}
              </button>
            );
          })}
        </div>
      </div>

      {err && (
        <div className="card-mil p-3 text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 flex items-start gap-2">
          <AlertTriangle size={14} /> {err}
        </div>
      )}
      {info && (
        <div className="card-mil p-3 text-sm text-[var(--success)] border border-[var(--success)]/40 bg-[var(--success)]/10 flex items-start gap-2">
          <CheckCircle2 size={14} /> {info}
        </div>
      )}

      {/* Calendário/agendamento */}
      <div className="grid md:grid-cols-2 gap-3" data-testid="schedule-cards">
        <ScheduleCard
          icon={CalendarClock}
          label="Última execução"
          value={last ? fmtDate(last.finished_at || last.started_at) : "—"}
          status={last?.status}
          mode={last?.collection_type}
          modeLabel={(modes.modes || []).find((m) => m.code === last?.collection_type)?.short_label}
        />
        <ScheduleCard
          icon={Clock}
          label="Próxima execução prevista"
          value={schedulerStatus?.next_run ? fmtDate(schedulerStatus.next_run) : "Não agendada"}
          subtitle={schedulerStatus?.scheduled ? `Intervalo: ${schedulerStatus.interval_days} dias` : "Scheduler inativo"}
        />
      </div>

      {/* Snapshot atual com breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="snapshot-publico">
        <Snap label="Documentos públicos" value={fmtNum(overview?.total_documents)} accent />
        <Snap label="Países agregados" value={fmtNum(overview?.total_countries)} />
        <Snap label="Últimos 7 dias" value={fmtNum(overview?.total_last_7_days)} />
        <Snap label="Vetor dominante" value={overview?.dominant_area_label || "—"} small />
        <Snap label="Maior volume" value={overview?.top_country || "—"} small />
      </div>

      {/* Distribuição por área */}
      {overview?.area_distribution && (
        <div className="card-mil p-4" data-testid="area-breakdown">
          <div className="label-mil text-[10px] mb-3">Distribuição por área temática</div>
          <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
            {Object.entries(overview.area_distribution).sort((a, b) => b[1] - a[1]).map(([code, v]) => (
              <div key={code} className="text-center p-2 bg-[var(--surface-2)] rounded">
                <div className="font-display font-bold text-lg text-[var(--gold)] tabular-nums">{v}</div>
                <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--text-muted)] mt-0.5 truncate" title={code}>
                  {code.length > 10 ? code.slice(0, 10) + "…" : code}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Última execução detalhada */}
      {last && (
        <div className="card-mil p-4 gold-accent-top" data-testid="last-run">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0 flex-1">
              <div className="label-mil text-[10px] flex items-center gap-1.5">
                <CalendarClock size={11} /> Detalhes da última execução
              </div>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm">
                <Field label="Início" value={fmtDate(last.started_at)} />
                <Field label="Conclusão" value={fmtDate(last.finished_at)} />
                <Field label="Disparado por" value={last.triggered_by || "—"} />
                <Field label="Usuário responsável" value={last.created_by || "—"} />
                <Field label="Duração" value={`${last.duration_seconds ?? "—"}s`} />
                <Field label="Status" value={
                  <span className={`badge text-[10px] ${
                    last.status === "success" ? "badge-success" :
                    last.status === "error" ? "badge-danger" : "badge-muted"
                  }`}>{last.status}</span>
                } />
                <Field label="Total processado" value={fmtNum(last.total_found)} />
                <Field label="Países agregados" value={fmtNum(last.total_by_country)} />
                <Field label="Vetores" value={fmtNum(last.total_by_area)} />
              </div>
              {last.notes && <div className="text-xs text-[var(--text-muted)] mt-3 italic">{last.notes}</div>}
              {last.error_message && (
                <div className="text-xs text-[var(--danger)] mt-2 p-2 bg-[var(--danger)]/10 border border-[var(--danger)]/30 rounded">
                  Erro: {last.error_message}
                </div>
              )}
              {(last.external_sources_used?.length > 0 || last.internal_sources_used?.length > 0) && (
                <div className="text-[10px] font-mono text-[var(--text-muted)] mt-3 space-y-0.5 pt-3 border-t border-[var(--border)]">
                  {last.external_sources_used?.length > 0 && (
                    <div><strong>Fontes externas:</strong> {last.external_sources_used.join(", ")}</div>
                  )}
                  {last.internal_sources_used?.length > 0 && (
                    <div><strong>Fontes internas:</strong> {last.internal_sources_used.join(", ")}</div>
                  )}
                </div>
              )}
            </div>
            <DataSourceBadge
              mode={last.collection_type}
              shortLabel={(modes.modes || []).find((m) => m.code === last.collection_type)?.short_label}
              note={last.source_description || last.collection_type_label}
            />
          </div>
        </div>
      )}

      {/* Histórico */}
      <div className="card-mil overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2">
          <History size={12} className="text-[var(--gold)]" />
          <div className="label-mil text-[10px]">Histórico de Execuções</div>
          <span className="text-[10px] text-[var(--text-muted)] font-mono ml-auto">
            {runs.length} {runs.length === 1 ? "registro" : "registros"}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)]">
              <tr>
                <th className="px-4 py-2 label-mil text-left">Data</th>
                <th className="px-4 py-2 label-mil text-left">Tipo</th>
                <th className="px-4 py-2 label-mil text-left">Status</th>
                <th className="px-4 py-2 label-mil text-right">Países</th>
                <th className="px-4 py-2 label-mil text-right">Vetores</th>
                <th className="px-4 py-2 label-mil text-right">Documentos</th>
                <th className="px-4 py-2 label-mil text-right">Duração</th>
                <th className="px-4 py-2 label-mil text-left">Origem</th>
                <th className="px-4 py-2 label-mil text-left">Observações</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)]/50">
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{fmtDateShort(r.started_at)}</td>
                  <td className="px-4 py-2">
                    <DataSourceBadge mode={r.collection_type} variant="minimal" withTooltip={false} />
                  </td>
                  <td className="px-4 py-2">
                    <span className={`badge text-[10px] ${
                      r.status === "success" ? "badge-success" :
                      r.status === "error" ? "badge-danger" : "badge-muted"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{r.total_by_country ?? "—"}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.total_by_area ?? "—"}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.total_found ?? "—"}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.duration_seconds ?? "—"}s</td>
                  <td className="px-4 py-2 text-xs">{r.triggered_by || "?"}</td>
                  <td className="px-4 py-2 text-[11px] text-[var(--text-muted)] max-w-xs truncate" title={r.error_message || r.notes || ""}>
                    {r.error_message ? <span className="text-[var(--danger)]">{r.error_message}</span> : (r.notes || "—")}
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-[var(--text-muted)] text-sm">
                  Nenhuma execução ainda. Clique em &ldquo;{MODE_BUTTON_LABEL[selectedMode]}&rdquo; para iniciar.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Snap({ label, value, small, accent }) {
  return (
    <div className={`card-mil p-4 ${accent ? "border-l-4 border-[var(--gold)]" : ""}`}>
      <div className="label-mil text-[10px]">{label}</div>
      <div className={`font-display font-bold mt-1 ${accent ? "text-[var(--gold)]" : "text-[var(--text)]"} ${small ? "text-base" : "text-2xl"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function ScheduleCard({ icon: Icon, label, value, subtitle, status, mode, modeLabel }) {
  return (
    <div className="card-mil p-4">
      <div className="label-mil text-[10px] flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </div>
      <div className="font-display font-bold text-lg mt-1.5 text-[var(--text)]">{value}</div>
      {subtitle && <div className="text-[11px] text-[var(--text-muted)] mt-0.5 font-mono">{subtitle}</div>}
      {(status || mode) && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {status && (
            <span className={`badge text-[10px] ${
              status === "success" ? "badge-success" :
              status === "error" ? "badge-danger" : "badge-muted"
            }`}>{status}</span>
          )}
          {mode && <DataSourceBadge mode={mode} shortLabel={modeLabel} variant="minimal" withTooltip={false} />}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-0.5">
      <span className="text-[11px] text-[var(--text-muted)]">{label}</span>
      <span className="text-[13px] text-[var(--text)] text-right">{value}</span>
    </div>
  );
}
